import DOMPurify from "dompurify";
import { EVENT_SUBCATEGORIES, OCCURRENCES_ACTIVE_STATUS_ID } from "@/components/program-manager/sessions/constants";
import { ONLINE_LOCATION } from "@/constants";
import { formatDate } from "@/mixins/helpers.js";
import { convertCentsToDolarAsCurrency } from "@/services/utils";
import { formatTimeFromDate } from "@/util/date.js";
import { findTimezoneByZone } from "@/util/timezones";

export const convertEventsToOccurrences = (events) => {
  const eventOccurrences = events
    .filter((val) => val?.status === "Active")
    .reduce((acc, event) => {
      let location = "";
      if (event?.type === "Online") {
        location = event?.online_location || "";
      } else {
        location =
          event?.event_locations
            ?.map((val) => {
              const address = val?.address ? `${val?.address?.city || ""}, ${val?.address?.state || ""}` : "";
              return address;
            })
            .join("\\<br\\>") || "";
      }
      const convertedOccurrences = event?.event_occurrences?.map((occurrence) => {
        return {
          title: event?.title || "",
          category: EVENT_SUBCATEGORIES[event.subcategory_id],
          category_name: occurrence?.associated_event?.event_category?.name || "",
          type: occurrence?.associated_event?.type,
          location,
          host: event?.host || "",
          start_date_time: formatDate(occurrence.start_date_time, "2-digit"),
          end_date_time: formatDate(occurrence.end_date_time, "2-digit"),
          price_student: occurrence.price_student || 0,
          id: occurrence.id,
          status_id: occurrence.status_id,
        };
      });
      const filteredOccurrences = convertedOccurrences.filter(
        (val) => val?.status_id === OCCURRENCES_ACTIVE_STATUS_ID,
      );
      return [...acc, ...filteredOccurrences];
    }, []);
  return eventOccurrences.sort((left, right) => left.title.localeCompare(right.title));
};

export const transformOccurrences = (occurrences) => {
  return (
    occurrences
      ?.reduce((acc, occurrence) => {
        if (occurrence?.status_id === OCCURRENCES_ACTIVE_STATUS_ID) {
          const occ = {
            title: occurrence?.associated_event?.title || "",
            category: EVENT_SUBCATEGORIES[occurrence.subcategory_id],
            category_name: occurrence?.associated_event?.event_category?.name || "",
            location: formatSanitizeLocations(
              occurrence?.associated_event?.event_locations,
              occurrence?.associated_event,
            ),
            departure_city: occurrence?.departure_city_name,
            departure_city_country_id: occurrence?.departure_city_country_id,
            type: occurrence?.associated_event?.type,
            host: occurrence?.associated_event?.host || "",
            start_date: formatDate(occurrence?.start_date_time, "short"),
            start_date_time: formatTimeFromDate(occurrence?.start_date_time),
            start_date_timezone: getTimeZoneOffset(occurrence?.time_zone),
            end_date_time: formatDate(occurrence.end_date_time, "2-digit"),
            price_student: convertCentsToDolarAsCurrency(occurrence.price_student) || 0,
            product_price: convertCentsToDolarAsCurrency(occurrence.product_price) || 0,
            id: occurrence.id,
            status_id: occurrence?.status_id,
            event_id: occurrence?.event_id,
            duration: formatDuration(occurrence?.associated_event),
            availability: formatTerms(occurrence?.terms_available),
          };

          return [...acc, occ];
        }
        return [...acc];
      }, [])
      ?.sort((left, right) => left.title.localeCompare(right.title)) || []
  );
};

const formatSanitizeLocations = (locations, associatedEvent) => {
  if (associatedEvent?.type === ONLINE_LOCATION) {
    const text = associatedEvent?.online_location || "";
    return DOMPurify.sanitize(text);
  }
  const location = locations
    ?.map((val) => {
      const address = val?.city ? `${val?.city?.city || ""}, ${val?.city?.country?.iso || ""}` : "";
      return address;
    })
    .filter(Boolean) // Remove empty strings if any
    .reduce((unique, item) => (unique.includes(item) ? unique : [...unique, item]), []) // Remove duplicates cities, iso
    .join("<br>");
  return DOMPurify.sanitize(location);
};
const formatDuration = (event_data) => {
  return event_data?.duration && event_data?.duration_units
    ? `${event_data?.duration} ${event_data?.duration_units}`
    : "";
};

const formatTerms = (terms) => {
  if (!terms) {
    return "n/a";
  }
  return terms
    ?.map((val) => {
      return val.name;
    })
    .join(", ");
};

const getTimeZoneOffset = (argTimeZone = "") => findTimezoneByZone(argTimeZone)?.offsetLabel ?? "";
